import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  Assignment,
  CheckCircleOutline,
  Close,
  Error,
  SwapHoriz,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { caseService } from '../services/api';

const formatDateTime = date => date
  ? new Date(date).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : 'Time unavailable';

const formatDate = date => date
  ? new Date(date).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
  : 'N/A';

const statusColors = {
  Open: { color: '#1565c0', bg: '#eaf3ff', border: '#90caf9' },
  Recommended: { color: '#2e7d32', bg: '#edf7ed', border: '#a5d6a7' },
  'Not Recommended': { color: '#c62828', bg: '#fff0f0', border: '#ef9a9a' },
  Processed: { color: '#2e7d32', bg: '#edf7ed', border: '#a5d6a7' },
  Reassigned: { color: '#b45309', bg: '#fff7e6', border: '#ffcc80' },
  Expired: { color: '#c62828', bg: '#fff0f0', border: '#ef9a9a' },
};

const statusIcons = {
  Started: <Assignment fontSize="small" />,
  Open: <Assignment fontSize="small" />,
  Recommended: <CheckCircleOutline fontSize="small" />,
  'Not Recommended': <Error fontSize="small" />,
  Processed: <CheckCircleOutline fontSize="small" />,
  Reassigned: <SwapHoriz fontSize="small" />,
  Expired: <Error fontSize="small" />,
};

const CaseDetailsDialog = ({ open, onClose, caseData, mode = 'details' }) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  useEffect(() => {
    if (!open || mode !== 'history' || !caseData?.web_reference_number) return;
    let active = true;
    setHistoryLoading(true);
    setHistoryError('');
    caseService.getCaseHistory(caseData.web_reference_number)
      .then(response => active && setHistory(response.data?.history || []))
      .catch(() => active && setHistoryError('Case history could not be loaded.'))
      .finally(() => active && setHistoryLoading(false));
    return () => { active = false; };
  }, [open, mode, caseData?.web_reference_number]);

  if (!caseData) return null;

  const statusStyle = statusColors[caseData.status] || statusColors.Open;
  const inspectionLink = `${window.location.origin}/${caseData.status === 'Reassigned' ? 'reassignment' : 'inspection'}/${caseData.web_reference_number}`;
  const details = [
    ['Web reference number', caseData.web_reference_number],
    ['Policy start date', formatDate(caseData.policy_start_date)],
    ['Policy expiry date', formatDate(caseData.policy_expiry_date)],
    ['Quote number', caseData.quote_number],
    ['Vehicle plate', [caseData.vehicle_plate_character, caseData.vehicle_plate].filter(Boolean).join(' ')],
    ['Engine number', caseData.engine_number],
    ['Chassis number', caseData.chassis_number],
    ['Make', caseData.make],
    ['Model', caseData.model],
    ['Body type', caseData.body_type],
    ['Manufacturing year', caseData.manufacturing_year],
    ['Reason for inspection', caseData.reason_for_inspection],
    ['Policy cover type', caseData.policy_cover_type],
    ['Cover details', caseData.cover_details],
    ['Previous policy expiry date', formatDate(caseData.previous_policy_expiry_date)],
    ['Previous policy cover type', caseData.previous_policy_cover_type],
    ...(caseData.status === 'Open' || caseData.status === 'Reassigned'
      ? [['Customer inspection link', <Typography component="a" href={inspectionLink} target="_blank" rel="noreferrer" variant="body2" sx={{ color: 'primary.main', overflowWrap: 'anywhere' }}>{inspectionLink}</Typography>]]
      : []),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={mode === 'history' ? 'sm' : 'md'}
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4">
              {mode === 'history' ? t('casesTable.caseHistory', { defaultValue: 'Case history' }) : 'Case details'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }} noWrap>
              {caseData.web_reference_number}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              size="small"
              icon={statusIcons[caseData.status] || <Assignment fontSize="small" />}
              label={t(`casesTable.${caseData.status.toLowerCase().replace(' ', '')}`)}
              sx={{ fontWeight: 600, color: statusStyle.color, bgcolor: statusStyle.bg, border: `1px solid ${statusStyle.border}` }}
            />
            <IconButton onClick={onClose} aria-label={t('common.close')} size="small"><Close /></IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {mode === 'details' ? (
          <Paper component="dl" variant="outlined" sx={{ m: 0, overflow: 'hidden' }}>
            {details.map(([label, value], index) => (
              <Box
                component="div"
                key={label}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(190px, 34%) minmax(0, 1fr)' },
                  gap: { xs: 0.25, sm: 2 },
                  px: 2,
                  py: 1.25,
                  bgcolor: index % 2 ? 'background.default' : 'background.paper',
                  borderBottom: index < details.length - 1 ? 1 : 0,
                  borderColor: 'divider',
                }}
              >
                <Typography component="dt" variant="body2" fontWeight={600}>{label}</Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, color: 'text.secondary', overflowWrap: 'anywhere' }}>
                  {value || 'N/A'}
                </Typography>
              </Box>
            ))}
          </Paper>
        ) : (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
          {historyLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Loading history…</Typography>
            </Box>
          ) : historyError ? (
            <Alert severity="error">{historyError}</Alert>
          ) : (
            <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {history.map((entry, index) => {
                const entryStyle = statusColors[entry.status] || statusColors.Open;
                return (
                  <Box
                    component="li"
                    key={`${entry.status}-${entry.actionTime}-${index}`}
                    sx={{ display: 'grid', gridTemplateColumns: '32px minmax(0, 1fr)', gap: 1.25, pb: index < history.length - 1 ? 2 : 0, position: 'relative' }}
                  >
                    {index < history.length - 1 && (
                      <Box sx={{ position: 'absolute', left: 15, top: 29, bottom: 2, width: 2, bgcolor: 'divider' }} />
                    )}
                    <Box sx={{ width: 32, height: 32, display: 'grid', placeItems: 'center', borderRadius: '50%', color: entryStyle.color, bgcolor: entryStyle.bg, border: `1px solid ${entryStyle.border}`, zIndex: 1 }}>
                      {statusIcons[entry.status] || <Assignment fontSize="small" />}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={700}>{entry.status}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDateTime(entry.actionTime)}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {entry.remarks || 'Status updated'} · {entry.actionBy || 'System'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit">{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaseDetailsDialog;
